import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAppDispatch } from '@/state/hooks'
import {
  EApplicationCard,
  EApplicationModal,
  EApplicationToast,
  EApplicationVerifyModal,
  TApplicationToastState,
  TApplicationVerifyState,
  TThemeOption,
  setApplicationLoading,
  setOpenModal,
  setOpenVerify,
  setShowCard,
  setTheme as setThemeReducer,
  setToast
} from './reducer'
import { AppState } from '@/state'

export function useModalOpen(modal: EApplicationModal): boolean {
  const openModal = useSelector(
    (state: AppState) => state.application.openModal
  )
  return openModal === modal
}

export function useOpenModal(modal: EApplicationModal): () => void {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setOpenModal(modal)), [dispatch, modal])
}

export function useToggleModal(modal: EApplicationModal): () => void {
  const open = useModalOpen(modal)
  const dispatch = useAppDispatch()
  return useCallback(
    () => dispatch(setOpenModal(open ? null : modal)),
    [dispatch, modal, open]
  )
}

export function useLoginModalToggle(): () => void {
  return useToggleModal(EApplicationModal.AUTH)
}
export function useBuyCryptoPreviewModalToggle(): () => void {
  return useToggleModal(EApplicationModal.CONFIRM_BUY_CRYPTO)
}

export function useSellCryptoPreviewModalToggle(): () => void {
  return useToggleModal(EApplicationModal.CONFIRM_SELL_CRYPTO)
}

export function usePromptpayPaymentReviewModalToggle(): () => void {
  return useToggleModal(EApplicationModal.PROMPTPAY_PAYMENT_REVIEW_MODAL)
}

export function useBuyEventNotPassModalToggle(): () => void {
  return useToggleModal(EApplicationModal.BUY_EVENT_NOT_PASS)
}

export function useSellCryptoTransactionModalToggle(): () => void {
  return useToggleModal(EApplicationModal.SELL_CRYPTO_TRANSACTION)
}

export function useTheme() {
  const currentTheme = useSelector(
    (state: AppState) => state.application.theme.value
  )
  const dispatch = useAppDispatch()

  const setTheme = (theme: TThemeOption) => {
    localStorage.setItem('theme', theme)
    dispatch(setThemeReducer(theme))
  }

  return [currentTheme, setTheme] as const
}

export const useShowCard = (card: EApplicationCard) => {
  const showCard = useSelector((state: AppState) => state.application.showCard)
  return showCard === card
}

export function useShow(card: EApplicationCard): () => void {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setShowCard(card)), [dispatch, card])
}

export function useShowCardWallet(): () => void {
  return useShow(EApplicationCard.WALLET_ADDRESS)
}

export function useShowCardSummary(): () => void {
  return useShow(EApplicationCard.SUMMARY)
}

export function useModalVerifyOpen(verify: EApplicationVerifyModal): boolean {
  const openModal = useSelector((state: AppState) => state.application.verify)
  return openModal?.verify === verify
}

export const useModal = () => {
  const dispatch = useDispatch()
  const openModal = useSelector((state: AppState) => state.application.verify)

  const closeModal = useCallback(() => {
    dispatch(setOpenVerify(null))
  }, [dispatch])

  const openVerifyModal = useCallback(
    (verify: TApplicationVerifyState) => {
      dispatch(setOpenVerify(verify))
    },
    [dispatch]
  )

  const toggleVerifyModal = useCallback(
    (verify: EApplicationVerifyModal) => {
      dispatch(
        setOpenVerify((prev: EApplicationVerifyModal | null) =>
          prev === verify ? null : verify
        )
      )
    },
    [dispatch]
  )

  const useToggleVerifyModal = (
    verify: TApplicationVerifyState
  ): ((props: TApplicationVerifyState) => void) => {
    const open = useModalVerifyOpen(verify.verify)
    return useCallback(
      () => dispatch(setOpenVerify(open ? null : verify)),
      [dispatch, verify, open]
    )
  }

  return {
    openModal,
    closeModal,
    openVerifyModal,
    toggleVerifyModal,
    useToggleVerifyModal
  }
}

export const useLoading = () => {
  const dispatch = useDispatch()
  const isApplicationLoading = useSelector(
    (state: AppState) => state.application.isApplicationLoading
  )

  const isLoading = useCallback(() => {
    dispatch(setApplicationLoading(true))
  }, [dispatch])

  const unLoading = useCallback(() => {
    dispatch(setApplicationLoading(false))
  }, [dispatch])

  return {
    isApplicationLoading,
    isLoading,
    unLoading
  }
}

export const useToastApplication = () => {
  const dispatch = useDispatch()
  const isToast = useSelector(
    (state: AppState) => state.application.toastApplication
  )

  const success = useCallback(
    ({ message }: Pick<TApplicationToastState, 'message'>) => {
      const isSuccess: TApplicationToastState = {
        open: true,
        type: EApplicationToast.SUCCESS,
        message: message
      }
      dispatch(setToast(isSuccess))
    },
    [dispatch]
  )

  const fail = useCallback(
    ({ message }: Pick<TApplicationToastState, 'message'>) => {
      const isFail: TApplicationToastState = {
        open: true,
        type: EApplicationToast.FAIL,
        message: message
      }
      dispatch(setToast(isFail))
    },
    [dispatch]
  )

  const toastClose = useCallback(() => {
    dispatch(setToast(null))
  }, [dispatch])

  return {
    isToast,
    success,
    fail,
    toastClose
  }
}
